import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { API_URL } from "./config";
import { refreshWowheadTooltips, wowheadItemUrl, wowheadItemUrlWithSetPieces } from "./wowhead";
import { getWowClassColor } from "./wowClassColor";
import type { FieldGroup } from "./plannerFieldIcons";
import { resolveSeatIconUrl } from "./plannerFieldIcons";
import GuildLootImportsTab from "./GuildLootImportsTab";
import LootCouncilAiPanel from "./LootCouncilAiPanel";
import { normalizeSlot } from "./lib/gearSlots";

import { tc, useLegible } from "./theme/legible";
import { DISPLAY } from "./alignmentUi";
type LootAwardRow = {
  // Award UUID - present on rows from /raids/:raidId/loot-council since
  // Phase 2D; required by the revoke action. Older callers that constructed
  // LootAwardRow objects locally would need to set this to "" but currently
  // every consumer comes from the server.
  id: string;
  awarded_at: string;
  bis_tag_at_award: string | null;
  external_item_id: string;
  loot_item_id: number | null;
  source: string;
  item_name: string | null;
  item_slot: string | null;
  guild_raid_id: string | null;
};

type EquippedItem = {
  slot_type: string;
  item_id: number;
  name: string;
  quality: string | null;
};

type EquipmentPayload = {
  items: EquippedItem[];
} | null;

type RosterRow = {
  character_id: string;
  character_name: string;
  class_name: string | null;
  primary_role: string | null;
  role_label: string | null;
  user_display_name: string | null;
  heat: number;
  loot_award_total?: number;
  recent_awards: LootAwardRow[];
  /** BNet equipment snapshot, mirrored from app_user_character.metadata.equipment. */
  equipment?: EquipmentPayload;
  /** ISO timestamp of the last BNet equipment refresh (drives 24h staleness). */
  equipment_last_updated?: string | null;
};

/** One slot normaliser for the whole app — see lib/gearSlots. */
const normEquipSlot = normalizeSlot;

const EQUIPMENT_STALE_MS = 24 * 60 * 60 * 1000; // 24h — matches the user-facing copy.

type LootItem = {
  id: number;
  external_item_id: string;
  name: string;
  quality: string | null;
  slot: string | null;
  boss_name: string | null;
  metadata: unknown;
  raid_option_id: number | null;
};

const NO_BOSS_FILTER_KEY = "__no_boss__";

/** NFC + trim so filter keys match DB/browser quirks (boss dropdown vs row compare). */
function normBossStr(s: string | null | undefined): string {
  return (s ?? "").trim().normalize("NFC");
}

function lootBossFilterKey(it: LootItem): string {
  const b = normBossStr(it.boss_name);
  return b.length > 0 ? b : NO_BOSS_FILTER_KEY;
}

type BisHit = { character_id: string; loot_item_id: number | string; tag: string };

/**
 * Per-character BIS interest summary for the focused loot row. `viaItem` is
 * non-null when the hit only matched because the selected row is a tier
 * token and the player BIS'd a class-specific piece that redeems from it
 * (see `bisForSelected` below).
 */
type BisHitInfo = {
  tag: string;
  /**
   * `slot` is needed for the equipped-item lookup in the council UI: when the
   * council member selects a tier token, we want to show what the player has
   * equipped in the *child* piece's slot, not the token's (which has no slot).
   */
  viaItem: { id: number; name: string; slot: string | null } | null;
};

/** DB / JSON may stringify integer ids; roster vs BIS character_id casing can differ. */
function sameLootRowId(a: unknown, b: unknown): boolean {
  return String(a) === String(b);
}

function normCharacterIdForBisMap(id: string): string {
  return id.trim().toLowerCase();
}

/**
 * Reusable hover/focus tooltip styled to match HeatHelpInfo's popover (the "?"
 * by the heat header). Use instead of native `title=` so affordances get the
 * same dark rounded-card treatment.
 */
function InfoTip({
  children,
  dot = "i",
  align = "center",
}: {
  children: React.ReactNode;
  dot?: string;
  align?: "left" | "center" | "right";
}) {
  const [open, setOpen] = useState(false);
  const hideTimer = useRef<number | null>(null);
  const cancelHide = useCallback(() => {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);
  const scheduleHide = useCallback(() => {
    cancelHide();
    hideTimer.current = window.setTimeout(() => {
      setOpen(false);
      hideTimer.current = null;
    }, 180);
  }, [cancelHide]);
  useEffect(() => () => cancelHide(), [cancelHide]);

  const posClass =
    align === "left" ? "left-0" : align === "right" ? "right-0" : "left-1/2 -translate-x-1/2";

  return (
    <span
      className="relative inline-flex items-center align-middle"
      onMouseEnter={() => {
        cancelHide();
        setOpen(true);
      }}
      onMouseLeave={scheduleHide}
    >
      <button
        type="button"
        aria-label="More info"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => {
          cancelHide();
          setOpen(true);
        }}
        onBlur={scheduleHide}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
        className="inline-flex items-center justify-center h-4 w-4 rounded-full border border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[9px] font-bold text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)] hover:border-[var(--c-ink-4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/45 transition-colors"
      >
        {dot}
      </button>
      {open ? (
        <div
          role="tooltip"
          className={`absolute z-40 top-full mt-2 ${posClass} w-[min(calc(100vw-2rem),17rem)] rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-2xl p-3 text-[11px] leading-snug text-[var(--c-ink-2)]`}
        >
          {children}
        </div>
      ) : null}
    </span>
  );
}

type Props = {
  raidId: string;
  guildId: string;
  raidName: string;
  onClose: () => void;
  showToast?: (m: string, t?: "success" | "error" | "info") => void;
};

/**
 * Small "?" affordance that opens a hover/focus popover explaining how heat
 * is calculated. Mirrors the formula implemented in `auth/heat_service.js`
 * (attendance value 10 · award costs 30/12/3 · 12-week half-life decay) and
 * the WCL-style percentile coloring done by `heatToParseScore` +
 * `lootCouncilParseColor` further down in this file. Keep those three in
 * sync if any constants change.
 */
function HeatHelpInfo() {
  const [open, setOpen] = useState(false);
  const hideTimer = useRef<number | null>(null);

  const cancelHide = useCallback(() => {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    cancelHide();
    hideTimer.current = window.setTimeout(() => {
      setOpen(false);
      hideTimer.current = null;
    }, 180);
  }, [cancelHide]);

  useEffect(() => () => cancelHide(), [cancelHide]);

  return (
    <div
      className="relative inline-flex items-center"
      onMouseEnter={() => {
        cancelHide();
        setOpen(true);
      }}
      onMouseLeave={scheduleHide}
    >
      <button
        type="button"
        aria-label="How heat is calculated"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => {
          cancelHide();
          setOpen(true);
        }}
        onBlur={scheduleHide}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
        className="ml-2 inline-flex items-center justify-center h-6 w-6 rounded-full border border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[11px] font-bold text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)] hover:border-[var(--c-ink-4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/45 transition-colors align-middle"
      >
        ?
      </button>
      {open ? (
        <div
          role="tooltip"
          className="absolute z-30 left-0 top-full mt-2 w-[min(calc(100vw-2rem),22rem)] rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-2xl p-4 text-[11px] leading-snug text-[var(--c-ink-2)] space-y-3"
        >
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800/85 dark:text-amber-200/85">How heat works</div>
            <p className="mt-1 text-[var(--c-ink-2)]">
              Heat measures who has been showing up and not getting items — higher heat means a stronger
              claim on the next drop.
            </p>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700/85 dark:text-emerald-300/85">Earned (attendance)</div>
            <ul className="mt-1 space-y-0.5 list-none pl-0">
              <li>
                <span className="font-mono text-emerald-800 dark:text-emerald-200">+10</span> per raid attended, counted once per
                raid per lockout week (taken from the raid's assignment roster).
              </li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700/85 dark:text-rose-300/85">Spent (awards)</div>
            <ul className="mt-1 space-y-0.5 list-none pl-0">
              <li>
                BIS <span className="font-mono text-rose-800 dark:text-rose-200">−30</span> · Upgrade{" "}
                <span className="font-mono text-rose-800 dark:text-rose-200">−12</span> · Sidegrade{" "}
                <span className="font-mono text-rose-800 dark:text-rose-200">−3</span>
              </li>
              <li className="text-[var(--c-ink-3)]">
                × slot weight for the player's role (weapons ≈ 2.0 · trinkets ≈ 1.5 · helm/chest/legs ≈ 1.3 ·
                default 1.0 · neck/wrist/rings ≈ 0.8)
              </li>
              <li className="text-[var(--c-ink-3)]">× the item's <code className="text-[var(--c-ink-2)]">raid_value_override</code> if one is set</li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700/85 dark:text-sky-300/85">Decay</div>
            <p className="mt-1 text-[var(--c-ink-2)]">
              Every event (attendance and award) is multiplied by{" "}
              <span className="font-mono text-[var(--c-ink)]">0.5^(weeks ÷ 12)</span> — a 12-week half-life.
              Something 12 weeks old counts half, 24 weeks old a quarter, and so on.
            </p>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700/85 dark:text-violet-300/85">Roster color tier</div>
            <p className="mt-1 text-[var(--c-ink-2)]">
              The heat pill is colored by the player's percentile within this raid's roster (WCL scale):
              grey &lt; 25 · green ≥ 25 · blue ≥ 50 · purple ≥ 75 · orange ≥ 95 · pink ≥ 99 · gold = 100.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CouncilSeatIcon({
  className,
  roleLabel,
  charData,
  fields,
}: {
  className: string | null;
  roleLabel: string | null;
  charData: any | null | undefined;
  fields: FieldGroup[] | null;
}) {
  const url = resolveSeatIconUrl(charData, className, roleLabel, fields);
  const color = getWowClassColor(className);
  const tip = [className, roleLabel].filter(Boolean).join(" · ");
  return (
    <div className="flex items-center shrink-0" title={tip || undefined}>
      {url ? (
        <img
          src={url}
          alt=""
          className="h-8 w-8 rounded border border-[var(--c-line)] object-cover bg-[var(--c-sunken)]"
          onError={(e) => {
            const el = e.currentTarget as HTMLImageElement;
            el.style.display = "none";
            const bar = el.nextElementSibling as HTMLElement | null;
            if (bar) bar.style.display = "block";
          }}
        />
      ) : null}
      <span
        className="h-8 w-1.5 rounded-full shrink-0"
        style={{ backgroundColor: color, display: url ? "none" : "block" }}
        aria-hidden
      />
    </div>
  );
}

function formatLootCouncilAwardDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function formatLootSource(source: string | null | undefined): string {
  const s = (source || "").toLowerCase();
  if (s === "rclc") return "RCLC";
  if (s === "manual") return "Manual";
  return source ? source : "—";
}

const COMPARE_PIN_MAX = 5;

/** Same tier thresholds as `getParseColor` in RosterPage.tsx (WCL-style). */
function lootCouncilParseColor(score: number): string {
  if (score === 100) return "#e5cc80";
  if (score >= 99) return "#e268a8";
  if (score >= 95) return "#ff8000";
  if (score >= 75) return "#a335ee";
  if (score >= 50) return "#0070dd";
  if (score >= 25) return "#1eff00";
  return "#9d9d9d";
}

/** Map heat to 0–100 vs full raid roster; max heat → 100 (gold). */
function heatToParseScore(heat: number, rosterForHeat: RosterRow[]): number {
  if (!rosterForHeat.length) return 0;
  const heats = rosterForHeat.map((r) => r.heat);
  const minH = Math.min(...heats);
  const maxH = Math.max(...heats);
  if (maxH === minH) return 100;
  const pct = ((heat - minH) / (maxH - minH)) * 100;
  return Math.round(Math.min(100, Math.max(0, pct)));
}

function LootHistoryAwardLine({
  a,
  raidId,
  canAssign,
  onRevoke,
}: {
  a: LootAwardRow;
  raidId: string;
  // canAssign + onRevoke are threaded all the way from the main panel so
  // the revoke button shows in both the hover popover and the compare-dock
  // cards. Same handler, same confirm flow.
  canAssign: boolean;
  onRevoke: (awardId: string, label: string) => void;
}) {
  const label = a.item_name?.trim() || `#${a.external_item_id || "?"}`;
  return (
    <li className="leading-snug">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[var(--c-ink)] flex-1 min-w-0 break-words">{label}</span>
        {canAssign && a.id ? (
          <button
            type="button"
            onClick={(e) => {
              // The roster row hover handler bubbles up otherwise and the
              // popover re-anchors itself mid-click - stop it here so the
              // confirm prompt opens cleanly.
              e.stopPropagation();
              onRevoke(a.id, label);
            }}
            className="shrink-0 rounded border border-rose-500/25 bg-rose-500/[0.06] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-800/70 dark:text-rose-200/70 hover:bg-rose-500/15 hover:text-rose-800 dark:hover:text-rose-100"
            title="Revoke this award (soft delete — preserved for audit)"
          >
            Revoke
          </button>
        ) : null}
      </div>
      <div className="text-[10px] text-[var(--c-ink-3)] mt-0.5">
        {formatLootCouncilAwardDate(a.awarded_at)}
        <span className="text-[var(--c-ink-4)]"> · </span>
        {formatLootSource(a.source)}
        {a.bis_tag_at_award ? (
          <>
            <span className="text-[var(--c-ink-4)]"> · </span>
            <span className="text-amber-800/80 dark:text-amber-200/80">list {a.bis_tag_at_award}</span>
          </>
        ) : null}
        {a.guild_raid_id === raidId ? (
          <>
            <span className="text-[var(--c-ink-4)]"> · </span>
            <span className="text-emerald-700/90 dark:text-emerald-300/90">this raid</span>
          </>
        ) : null}
      </div>
    </li>
  );
}

/** Shared body: hover tooltip + compare dock columns. */
function LootHistoryPanelContent({
  row,
  raidId,
  selectedItem,
  listTag,
  showTitle,
  showFootnote,
  heatColorHex,
  canAssign,
  onRevoke,
}: {
  row: RosterRow;
  raidId: string;
  selectedItem: LootItem | null;
  listTag: string | null;
  showTitle: boolean;
  showFootnote: boolean;
  /** Parse-style color for this row's heat (from full-roster percentile). */
  heatColorHex: string;
  canAssign: boolean;
  onRevoke: (awardId: string, label: string) => void;
}) {
  const recent = row.recent_awards ?? [];
  const total = row.loot_award_total ?? recent.length;
  const last5 = recent.slice(0, 5);
  const awardsThisRaidInWindow = recent.filter((a) => a.guild_raid_id === raidId).length;

  const extSel = String(selectedItem?.external_item_id ?? "").trim();
  const idSel = selectedItem?.id;

  const samePiece =
    selectedItem != null
      ? recent.filter(
          (a) =>
            (a.loot_item_id != null && sameLootRowId(a.loot_item_id, idSel)) ||
            (extSel.length > 0 && String(a.external_item_id || "").trim() === extSel)
        )
      : [];

  const slotNorm = (selectedItem?.slot || "").trim().toLowerCase();
  const sameSlotOther =
    selectedItem != null && slotNorm.length > 0
      ? recent
          .filter((a) => {
            const slotA = (a.item_slot || "").trim().toLowerCase();
            if (slotA !== slotNorm) return false;
            const sameId = a.loot_item_id != null && sameLootRowId(a.loot_item_id, idSel);
            const sameExt = extSel.length > 0 && String(a.external_item_id || "").trim() === extSel;
            return !sameId && !sameExt;
          })
          .slice(0, 6)
      : [];

  return (
    <div className="text-[11px] text-[var(--c-ink)] leading-snug space-y-2">
      {showTitle ? (
        <>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)]">Loot history</div>
          <div className="font-semibold text-[var(--c-ink)] truncate">{row.character_name}</div>
        </>
      ) : null}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-[var(--c-ink-3)] pb-2 border-b border-[var(--c-line-soft)]">
        <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5" style={{ backgroundColor: `${heatColorHex}12` }}>
          <span className="text-[var(--c-ink-4)]">Heat</span>{" "}
          <span className="font-mono font-semibold tabular-nums" style={{ color: tc(heatColorHex) }}>
            {row.heat}
          </span>
        </span>
        <span>
          <span className="text-[var(--c-ink-4)]">Items (guild)</span>{" "}
          <span className="font-bold text-[var(--c-ink)] tabular-nums">{total}</span>
        </span>
        {awardsThisRaidInWindow > 0 ? (
          <span>
            <span className="text-[var(--c-ink-4)]">This raid (in window)</span>{" "}
            <span className="text-emerald-800/90 dark:text-emerald-200/90 font-bold tabular-nums">{awardsThisRaidInWindow}</span>
            <span className="text-[var(--c-ink-4)]"> /25</span>
          </span>
        ) : null}
      </div>
      {selectedItem && listTag ? (
        <div className="text-[10px] text-amber-800/90 dark:text-amber-100/90 rounded-lg border border-amber-500/25 bg-amber-500/10 px-2 py-1.5">
          <span className="text-[var(--c-ink-3)]">BIS tag for focus item:</span>{" "}
          <span className="font-bold uppercase">{listTag}</span>
        </div>
      ) : null}
      {selectedItem && samePiece.length > 0 ? (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700/80 dark:text-sky-300/80 mb-1">This piece</div>
          <ul className="space-y-1.5 list-none pl-0 m-0">
          {samePiece.map((a, i) => (
              <LootHistoryAwardLine key={`piece-${a.loot_item_id}-${a.awarded_at}-${i}`} a={a} raidId={raidId} canAssign={canAssign} onRevoke={onRevoke} />
            ))}
          </ul>
        </div>
      ) : selectedItem ? (
        <p className="text-[10px] text-[var(--c-ink-3)] m-0">
          No award for this exact item in their 25 most recent logged drops.
          {total > 25 ? (
            <span className="block mt-1 text-[var(--c-ink-4)]">
              {row.character_name} has {total} lifetime guild awards — a match could be older than this window.
            </span>
          ) : null}
        </p>
      ) : null}
      {selectedItem && sameSlotOther.length > 0 ? (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700/80 dark:text-violet-300/80 mb-1">
            Same slot ({selectedItem.slot})
          </div>
          <ul className="space-y-1.5 list-none pl-0 m-0">
          {sameSlotOther.map((a, i) => (
              <LootHistoryAwardLine key={`slot-${a.loot_item_id}-${a.awarded_at}-${i}`} a={a} raidId={raidId} canAssign={canAssign} onRevoke={onRevoke} />
            ))}
          </ul>
        </div>
      ) : null}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)] mb-1">Last 5 awards</div>
        {last5.length === 0 ? (
          <p className="text-[10px] text-[var(--c-ink-3)] m-0">No recorded awards yet.</p>
        ) : (
          <ul className="space-y-1.5 list-none pl-0 m-0">
            {last5.map((a, i) => (
              <LootHistoryAwardLine key={`last-${a.loot_item_id}-${a.awarded_at}-${i}`} a={a} raidId={raidId} canAssign={canAssign} onRevoke={onRevoke} />
            ))}
          </ul>
        )}
      </div>
      {showFootnote ? (
        <p className="text-[9px] text-[var(--c-ink-4)] m-0 leading-relaxed">
          Recent list shows up to 25 newest awards per character; totals are lifetime guild awards.
        </p>
      ) : null}
    </div>
  );
}

function RosterLootHistoryPopover({
  row,
  raidId,
  selectedItem,
  listTag,
  heatColorHex,
  anchor,
  onPointerEnter,
  onPointerLeave,
  canAssign,
  onRevoke,
}: {
  row: RosterRow;
  raidId: string;
  selectedItem: LootItem | null;
  listTag: string | null;
  heatColorHex: string;
  anchor: { left: number; top: number };
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  canAssign: boolean;
  onRevoke: (awardId: string, label: string) => void;
}) {
  return createPortal(
    <div
      role="tooltip"
      data-roster-popover-open="true"
      className="fixed z-[100000] w-[min(calc(100vw-16px),19rem)] rounded-2xl border border-[var(--c-line)] bg-[var(--c-sunken)] shadow-2xl p-3 pointer-events-auto"
      style={{ left: anchor.left, top: anchor.top, transform: "translateY(-50%)" }}
      onMouseEnter={onPointerEnter}
      onMouseLeave={onPointerLeave}
    >
      <LootHistoryPanelContent
        row={row}
        raidId={raidId}
        selectedItem={selectedItem}
        listTag={listTag}
        showTitle
        showFootnote
        heatColorHex={heatColorHex}
        canAssign={canAssign}
        onRevoke={onRevoke}
      />
    </div>,
    document.body
  );
}

function lootQualityBarClass(quality: string | null | undefined): string {
  const q = String(quality ?? "").trim();
  if (q === "6") return "from-amber-200/90 to-amber-600/30";
  if (q === "5") return "from-orange-300/80 to-orange-600/25";
  if (q === "4") return "from-purple-300/70 to-purple-700/25";
  if (q === "3") return "from-sky-300/60 to-blue-700/20";
  if (q === "2") return "from-emerald-300/50 to-emerald-800/20";
  return "from-[var(--c-sunken-2)] to-[var(--c-bg-veil)]";
}

/**
 * BoP trade window — only awards from the last 2 hours are eligible to be
 * traded in-game (Anniversary rules). The encoder filters by this client-side
 * so the addon can trust the received set without re-running the math.
 */
const RALLIFY_BOP_WINDOW_MS = 2 * 60 * 60 * 1000;

/**
 * Wrap a loot bucket in the Rallify addon envelope. The UTF-8-safe base64 trick
 * keeps player names with accented characters intact across the round trip.
 * (It used to mirror `toRallifyMrtCode` in RaidAssignmentsPage, which the raid
 * sheet rebuild removed; this is now the only copy.)
 *
 * The addon dedupes on `award_id`, so re-pasting an overlapping bucket is a
 * no-op for already-loaded awards (their local `status` is preserved).
 */
function toRallifyLootCode(args: {
  raidId: string;
  raidName: string;
  awards: Array<{
    id: string;
    character_name: string;
    external_item_id: string;
    item_name: string | null;
    item_quality: string | null;
    awarded_at: string;
    bis_tag_at_award: string | null;
  }>;
}): string {
  // external_item_id is stored as text in `loot_award` but the addon expects
  // a wowhead numeric id — coerce, defaulting to 0 for any non-numeric edge case.
  const itemIdAsNumber = (s: string) => {
    const n = Number(String(s).trim());
    return Number.isFinite(n) ? n : 0;
  };
  // `awarded_at` is an ISO timestamptz from Postgres; the addon stores it as
  // epoch seconds (matches `time()` in WoW Lua).
  const awardedAtAsEpoch = (iso: string) => Math.floor(new Date(iso).getTime() / 1000);

  const payload = {
    raid_id: args.raidId,
    raid_name: args.raidName,
    guild_name: "",
    generated: Math.floor(Date.now() / 1000),
    awards: args.awards.map((a) => ({
      award_id: a.id,
      recipient: a.character_name,
      item_id: itemIdAsNumber(a.external_item_id),
      item_name: a.item_name ?? "",
      item_quality: a.item_quality ?? "",
      awarded_at: awardedAtAsEpoch(a.awarded_at),
      bis_tag: a.bis_tag_at_award ?? null,
    })),
  };
  const json = JSON.stringify(payload);
  const b64 = btoa(unescape(encodeURIComponent(json)));
  return `RLY|LOOT|v1|${b64}`;
}

function EquippedSlotIcon({
  item,
  gameCode,
  equipmentItems,
}: {
  item: EquippedItem;
  gameCode: string | null;
  equipmentItems: EquippedItem[];
}) {
  const ownedIds = equipmentItems.map((i) => i.item_id);
  const href = wowheadItemUrlWithSetPieces(item.item_id, gameCode, ownedIds);

  return (
    <a
    href={href}
    target="_blank"
    rel="noreferrer"
    data-wh-icon-size="small"
    data-wh-rename-link="false"
    aria-label={`Currently equipped: ${item.name}`}
    onClick={(e) => e.stopPropagation()}
    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] hover:border-sky-400/45 hover:bg-sky-500/10 transition-colors shrink-0"
  >
    <span className="sr-only">{item.name}</span>
  </a>
  );
}

function EquippedSlotSyncButton({
  isLoading,
  isStale,
  onSync,
}: {
  isLoading: boolean;
  isStale: boolean;
  onSync: () => void;
}) {
  const titleText = isLoading
    ? "Refreshing equipment from Battle.net…"
    : isStale
      ? "Equipped item snapshot is over 24 hours old — click to refresh from Battle.net"
      : "No equipped item on file for this slot — click to refresh from Battle.net";
  const syncBtnClass = isLoading
    ? "inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors shrink-0 border-sky-400/40 bg-sky-500/15 text-sky-800/85 dark:text-sky-200/85 cursor-wait"
    : "inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors shrink-0 border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:border-sky-400/45 hover:bg-sky-500/10 hover:text-sky-800 dark:hover:text-sky-200";

  return (
    <button
      type="button"
      onClick={(ev) => {
        ev.stopPropagation();
        if (!isLoading) onSync();
      }}
      disabled={isLoading}
      aria-label={titleText}
      title={titleText}
      className={syncBtnClass}
    >
      {isLoading ? (
        <svg
          className="h-4 w-4 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
          <path
            d="M21 12a9 9 0 0 1-9 9"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      ) : (
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 12a9 9 0 0 1 15.5-6.3M21 4v5h-5M21 12a9 9 0 0 1-15.5 6.3M3 20v-5h5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}

export default function PlannerLootCouncilPanel({ raidId, guildId, raidName, onClose, showToast }: Props) {
  useLegible();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [lootItems, setLootItems] = useState<LootItem[]>([]);
  const [bisInterest, setBisInterest] = useState<BisHit[]>([]);
  const [gameCode, setGameCode] = useState<string | null>(null);
  /** Officer / raid leader / loot master — only these roles may assign gear and
   * mutate raid loot settings. Read access is open to any guild member. */
  const [canAssign, setCanAssign] = useState(false);

  const [itemSearch, setItemSearch] = useState("");
  const [bossFilterKey, setBossFilterKey] = useState<string | null>(null);
  const [selectedLootId, setSelectedLootId] = useState<number | string | null>(null);

  const [awardCharId, setAwardCharId] = useState<string | null>(null);
  const [awardNote, setAwardNote] = useState("");
  const [awardHeatTag, setAwardHeatTag] =
    useState<"none" | "sidegrade" | "upgrade" | "bis">("none");
  // "Award someone else": search box over the FULL roster so the RL can award
  // the selected item to a player who never tagged it. Kept separate from the
  // interested list so that list stays focused and heat-sorted.
  const [awardSearchOpen, setAwardSearchOpen] = useState(false);
  const [awardSearchQuery, setAwardSearchQuery] = useState("");
  const [awardSubmitting, setAwardSubmitting] = useState(false);

  // ── Sync-to-addon modal state ─────────────────────────────────────────────
  // We don't fetch on panel mount — the modal pulls fresh awards on open so
  // there's no extra request on every council session and no stale data risk.
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncAwards, setSyncAwards] = useState<Array<{
    id: string;
    character_name: string;
    external_item_id: string;
    item_name: string | null;
    item_quality: string | null;
    awarded_at: string;
    bis_tag_at_award: string | null;
    guild_raid_id: string | null;
  }> | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncCopied, setSyncCopied] = useState(false);

  const openSyncModal = useCallback(async () => {
    setSyncOpen(true);
    setSyncLoading(true);
    setSyncError(null);
    setSyncAwards(null);
    setSyncCopied(false);
    try {
      // Reuses the existing GET endpoint (loot_award_routes.js:669) which already
      // gates on assertGuildLootCouncilAccess and joins raid_loot_item for
      // item_name/quality. We filter to this raid + the 2h window client-side.
      // Revoked awards are excluded server-side (revoked_at IS NULL is the
      // default filter); we additionally drop already-traded awards here so
      // the addon doesn't see anything the LM has already handed over.
      const res = await fetch(`${API_URL}/guilds/${encodeURIComponent(guildId)}/loot-awards`, {
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      const all: any[] = Array.isArray(body?.awards) ? body.awards : [];
      const cutoff = Date.now() - RALLIFY_BOP_WINDOW_MS;
      const inWindow = all.filter(
        (a) =>
          a.guild_raid_id === raidId &&
          !a.traded_at &&
          new Date(a.awarded_at).getTime() >= cutoff
      );
      setSyncAwards(inWindow);
    } catch (e: any) {
      setSyncError(e?.message || "Failed to load awards");
    } finally {
      setSyncLoading(false);
    }
  }, [guildId, raidId]);

  /** Game fields + raid candidates — same inputs Planner uses for class/spec icons on roster cards. */
  const [gameFields, setGameFields] = useState<FieldGroup[] | null>(null);
  const [candidateById, setCandidateById] = useState<Map<string, any>>(() => new Map());

  const [rosterTip, setRosterTip] = useState<{
    row: RosterRow;
    listTag: string | null;
    left: number;
    top: number;
  } | null>(null);
  const rosterTipTimer = useRef<number | null>(null);

  const clearRosterTipTimer = useCallback(() => {
    if (rosterTipTimer.current != null) {
      window.clearTimeout(rosterTipTimer.current);
      rosterTipTimer.current = null;
    }
  }, []);

  const scheduleHideRosterTip = useCallback(() => {
    clearRosterTipTimer();
    rosterTipTimer.current = window.setTimeout(() => {
      setRosterTip(null);
      rosterTipTimer.current = null;
    }, 200);
  }, [clearRosterTipTimer]);

  const showRosterTip = useCallback(
    (payload: { row: RosterRow; listTag: string | null; left: number; top: number }) => {
      clearRosterTipTimer();
      setRosterTip(payload);
    },
    [clearRosterTipTimer]
  );

  useEffect(() => () => clearRosterTipTimer(), [clearRosterTipTimer]);

  /** Pin character IDs for side-by-side compare (same stats as hover loot history). */
  const [pinnedForCompare, setPinnedForCompare] = useState<string[]>([]);

  useEffect(() => {
    setPinnedForCompare((prev) => prev.filter((id) => roster.some((r) => r.character_id === id)));
  }, [roster]);

  const toggleComparePin = useCallback(
    (characterId: string) => {
      setPinnedForCompare((prev) => {
        if (prev.includes(characterId)) return prev.filter((x) => x !== characterId);
        if (prev.length >= COMPARE_PIN_MAX) {
          showToast?.(`You can pin up to ${COMPARE_PIN_MAX} players to compare.`, "info");
          return prev;
        }
        return [...prev, characterId];
      });
    },
    [showToast]
  );

  /**
   * Tracks which roster rows are currently mid-refresh against BNet. A Set
   * (not a single id) so multiple syncs can run in parallel without one
   * overwriting another's spinner state. Each entry is the character_id.
   */
  const [syncingEquipmentByChar, setSyncingEquipmentByChar] = useState<Set<string>>(
    () => new Set()
  );

  /**
   * Refresh one character's equipment snapshot from the server (which proxies
   * BNet). On success we patch only that row in `roster` so React rerenders
   * just the affected card — no full reload, no scroll jump, and the existing
   * `useEffect(refreshWowheadTooltips, [..., roster, ...])` picks up the new
   * <a data-wh-icon-size> element so its tooltip wires up.
   */
  const handleSyncEquipment = useCallback(
    async (characterId: string) => {
      setSyncingEquipmentByChar((prev) => {
        if (prev.has(characterId)) return prev;
        const next = new Set(prev);
        next.add(characterId);
        return next;
      });
      try {
        const res = await fetch(
          `${API_URL}/raids/${encodeURIComponent(raidId)}/loot-council/refresh-equipment/${encodeURIComponent(characterId)}`,
          { method: "POST", credentials: "include" }
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(body?.error || body?.message || `HTTP ${res.status}`);
        }
        setRoster((prev) =>
          prev.map((r) =>
            r.character_id === characterId
              ? {
                  ...r,
                  equipment: body.equipment ?? null,
                  equipment_last_updated: body.equipment_last_updated ?? null,
                }
              : r
          )
        );
      } catch (e: any) {
        showToast?.(
          e?.message
            ? `Couldn't refresh equipment — ${e.message}`
            : "Couldn't refresh equipment.",
          "error"
        );
      } finally {
        setSyncingEquipmentByChar((prev) => {
          if (!prev.has(characterId)) return prev;
          const next = new Set(prev);
          next.delete(characterId);
          return next;
        });
      }
    },
    [raidId, showToast]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    setGameFields(null);
    setCandidateById(new Map());
    setCanAssign(false);
    try {
      const res = await fetch(`${API_URL}/raids/${encodeURIComponent(raidId)}/loot-council`, {
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setRoster(Array.isArray(data.roster) ? data.roster : []);
      setLootItems(Array.isArray(data.loot_items) ? data.loot_items : []);
      setBisInterest(Array.isArray(data.bis_interest) ? data.bis_interest : []);
      setGameCode(data.raid?.game_code ?? null);
      setCanAssign(data.can_assign === true);

      const gameId = data.raid?.game_id;
      if (gameId) {
        void (async () => {
          try {
            const [fRes, cRes] = await Promise.all([
              fetch(`${API_URL}/games/${encodeURIComponent(String(gameId))}/fields`, { credentials: "include" }),
              fetch(`${API_URL}/raids/${encodeURIComponent(raidId)}/candidates`, { credentials: "include" }),
            ]);
            const fd = fRes.ok ? await fRes.json() : [];
            const cd = cRes.ok ? await cRes.json() : [];
            setGameFields(Array.isArray(fd) ? fd : []);
            const m = new Map<string, any>();
            for (const row of Array.isArray(cd) ? cd : []) {
              if (row?.characterId) m.set(row.characterId, row);
            }
            setCandidateById(m);
          } catch {
            setGameFields([]);
            setCandidateById(new Map());
          }
        })();
      } else {
        setGameFields([]);
        setCandidateById(new Map());
      }
    } catch (e: any) {
      setErr(e?.message || "Failed to load loot council");
    } finally {
      setLoading(false);
    }
  }, [raidId]);

  /**
   * Revoke an award. Calls the DELETE endpoint shipped in Phase 2C, which
   * soft-deletes the row (sets revoked_at + writes the reason into raw).
   * The native window.prompt double-duties as confirmation: pressing OK
   * confirms the revoke, Cancel aborts. Idempotent server-side so a
   * double-click is harmless.
   *
   * We close the hover popover (setRosterTip) so the user immediately sees
   * the panel update, then await load() to refresh roster heat, recent
   * awards, and totals - the revoked row should be gone on next render.
   */
  const handleRevokeAward = useCallback(
    async (awardId: string, label: string) => {
      const reason = window.prompt(
        `Revoke this award?\n\n  ${label}\n\nOptional reason (press OK to revoke, Cancel to keep):`,
        ""
      );
      if (reason === null) return;
      try {
        const res = await fetch(
          `${API_URL}/guilds/${encodeURIComponent(guildId)}/loot-awards/${encodeURIComponent(awardId)}`,
          {
            method: "DELETE",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason: reason.trim() }),
          }
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
        if (body.revoked === false) {
          showToast?.("That award was already revoked.", "info");
        } else {
          showToast?.("Award revoked.", "success");
        }
        setRosterTip(null);
        await load();
      } catch (e: any) {
        showToast?.(e?.message || "Revoke failed", "error");
      }
    },
    [guildId, load, showToast]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(refreshWowheadTooltips, 200);
    return () => clearTimeout(t);
  }, [lootItems, roster, selectedLootId, bossFilterKey, itemSearch, gameFields, candidateById]);

  const bossFilterOptions = useMemo(() => {
    const labels = new Map<string, string>();
    for (const it of lootItems) {
      const k = lootBossFilterKey(it);
      if (!labels.has(k)) {
        labels.set(k, k === NO_BOSS_FILTER_KEY ? "Other / no boss" : normBossStr(it.boss_name));
      }
    }
    return Array.from(labels.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], undefined, { sensitivity: "base" })
    );
  }, [lootItems]);

  useEffect(() => {
    if (bossFilterKey === null) return;
    const ok = lootItems.some((it) => lootBossFilterKey(it) === bossFilterKey);
    if (!ok) setBossFilterKey(null);
  }, [lootItems, bossFilterKey]);

  /** Boss filter hides non-matching rows — clear selection so focus + list stay consistent. */
  useEffect(() => {
    if (bossFilterKey === null || selectedLootId == null || lootItems.length === 0) return;
    const sel = lootItems.find((x) => sameLootRowId(x.id, selectedLootId));
    if (!sel || lootBossFilterKey(sel) !== bossFilterKey) {
      setSelectedLootId(null);
      setAwardCharId(null);
      setAwardNote("");
    }
  }, [bossFilterKey, lootItems, selectedLootId]);

  const filteredItems = useMemo(() => {
    let list = lootItems;
    if (bossFilterKey !== null) {
      list = list.filter((it) => lootBossFilterKey(it) === bossFilterKey);
    }
    const q = itemSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((it) => {
      const idStr = String(it.external_item_id || "");
      if (idStr.includes(q)) return true;
      if (it.name?.toLowerCase().includes(q)) return true;
      if (it.boss_name?.toLowerCase().includes(q)) return true;
      const meta = it.metadata as Record<string, unknown> | null;
      const tok = meta?.redeemed_from_item_id;
      if (typeof tok === "number" && String(tok).includes(q)) return true;
      return false;
    });
  }, [lootItems, itemSearch, bossFilterKey]);

  /**
   * BIS interest for the currently focused loot row, including the "token →
   * tier piece" relationship.
   *
   * Tier tokens (e.g. "Helm of the Vanquished Champion", item 30242) drop in
   * the raid, but players never put the token itself on their BIS list —
   * they list the class-specific helm that the token redeems into (e.g.
   * "Cyclone Faceguard" for Shaman). Each class piece row in raid_loot_item
   * carries `metadata.redeemed_from_item_id = <token loot_item_id>`. When the
   * token is selected here we must surface BIS hits on those child pieces so
   * the council can see who actually wants the token and award it to them.
   *
   * The award is still recorded against the token's external_item_id /
   * loot_item_id (see submitManualAward) — only the interest matching uses
   * the child relationship. The matched child item is carried on the hit so
   * the roster row can show "(via Cyclone Faceguard)".
   */
  const bisForSelected = useMemo(() => {
    const empty = new Map<string, BisHitInfo>();
    if (selectedLootId == null) return empty;
    const sel = lootItems.find((x) => sameLootRowId(x.id, selectedLootId));
    if (!sel) return empty;
    const selectedExt = String(sel.external_item_id || "").trim();

    const childItems: LootItem[] = lootItems.filter((it) => {
      const meta = it.metadata as Record<string, unknown> | null;
      const parentId = meta?.redeemed_from_item_id;
      return parentId != null && sameLootRowId(parentId, sel.id);
    });

    const tagRank: Record<string, number> = { bis: 0, upgrade: 1, sidegrade: 2 };
    const m = new Map<string, BisHitInfo>();

    for (const b of bisInterest) {
      let direct = sameLootRowId(b.loot_item_id, selectedLootId);
      if (!direct && selectedExt.length > 0) {
        const rowForBis = lootItems.find((it) => sameLootRowId(it.id, b.loot_item_id));
        if (rowForBis && String(rowForBis.external_item_id || "").trim() === selectedExt) {
          direct = true;
        }
      }
      const viaChild: LootItem | null = direct
        ? null
        : childItems.find((c) => sameLootRowId(b.loot_item_id, c.id)) ?? null;
      if (!direct && !viaChild) continue;

      const key = normCharacterIdForBisMap(b.character_id);
      const next: BisHitInfo = {
        tag: b.tag,
        viaItem: viaChild
          ? { id: viaChild.id, name: viaChild.name, slot: viaChild.slot ?? null }
          : null,
      };
      const prev = m.get(key);
      // Same character can have hits on both the token and a child piece;
      // keep the strongest tag (bis > upgrade > sidegrade), and prefer the
      // direct hit (no viaItem) when tags are equal.
      if (!prev) {
        m.set(key, next);
      } else {
        const prevRank = tagRank[prev.tag] ?? 99;
        const nextRank = tagRank[next.tag] ?? 99;
        if (nextRank < prevRank) m.set(key, next);
        else if (nextRank === prevRank && prev.viaItem && !next.viaItem) m.set(key, next);
      }
    }
    return m;
  }, [bisInterest, selectedLootId, lootItems]);

  /** Children that redeem from the currently selected loot row, if any. */
  const tokenChildren = useMemo(() => {
    if (selectedLootId == null) return [] as LootItem[];
    return lootItems.filter((it) => {
      const meta = it.metadata as Record<string, unknown> | null;
      const parentId = meta?.redeemed_from_item_id;
      return parentId != null && sameLootRowId(parentId, selectedLootId);
    });
  }, [lootItems, selectedLootId]);

  const displayedRoster = useMemo(() => {
    if (selectedLootId == null) return roster;
    const tagMap = bisForSelected;
    return roster.filter((r) => tagMap.has(normCharacterIdForBisMap(r.character_id)));
  }, [roster, selectedLootId, bisForSelected]);

  const aiCandidateNames = useMemo(
    () => displayedRoster.map((r) => r.character_name),
    [displayedRoster]
  );

  const importMembers = useMemo(
    () => roster.map((r) => ({ id: r.character_id, name: r.character_name })),
    [roster]
  );

  const selectedItem: LootItem | null =
    selectedLootId != null
      ? (lootItems.find((x) => sameLootRowId(x.id, selectedLootId)) ?? null)
      : null;

  const interestBreakdown = useMemo(() => {
    if (selectedLootId == null) return null;
    let bis = 0;
    let upgrade = 0;
    let sidegrade = 0;
    bisForSelected.forEach((hit) => {
      if (hit.tag === "bis") bis++;
      else if (hit.tag === "upgrade") upgrade++;
      else if (hit.tag === "sidegrade") sidegrade++;
    });
    const total = bis + upgrade + sidegrade;
    return { bis, upgrade, sidegrade, total };
  }, [selectedLootId, bisForSelected]);

  const pickedRosterRow = useMemo(
    () => (awardCharId ? roster.find((r) => r.character_id === awardCharId) ?? null : null),
    [awardCharId, roster]
  );

  const pickedHitInfo = useMemo(() => {
    if (!awardCharId || selectedLootId == null) return null;
    return bisForSelected.get(normCharacterIdForBisMap(awardCharId)) ?? null;
  }, [awardCharId, selectedLootId, bisForSelected]);
  const pickedListTag = pickedHitInfo?.tag ?? null;
  const pickedViaItemName = pickedHitInfo?.viaItem?.name ?? null;

  /** Parse-style hex per character from heat vs full raid roster (API order irrelevant). */
  const heatColorByCharacterId = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of roster) {
      const score = heatToParseScore(r.heat, roster);
      m.set(r.character_id, lootCouncilParseColor(score));
    }
    return m;
  }, [roster]);

  const cancelAwardDraft = () => {
    setAwardCharId(null);
    setAwardNote("");
    setAwardHeatTag("none");
    setAwardSearchOpen(false);
    setAwardSearchQuery("");
  };

  // Heat defaults to the player's own list tag (incl. tier-token resolution via
  // bisForSelected); players with no tag for this item — i.e. "award other"
  // picks — default to "none" (zero heat). RL can override in the confirm bar.
  const defaultHeatTagFor = (
    charId: string
  ): "none" | "sidegrade" | "upgrade" | "bis" => {
    const t = bisForSelected.get(normCharacterIdForBisMap(charId))?.tag;
    return t === "bis" || t === "upgrade" || t === "sidegrade" ? t : "none";
  };

  // Single entry point for choosing the recipient — used by both the
  // interested-list PICK button and the "award other" search results.
  const pickPlayerForAward = (charId: string) => {
    setAwardCharId(charId);
    setAwardHeatTag(defaultHeatTagFor(charId));
    setAwardSearchOpen(false);
    setAwardSearchQuery("");
  };

  // Autocomplete candidates for the "award other" search: full roster, name
  // contains query (case-insensitive), capped so the dropdown stays crisp.
  const awardSearchResults = useMemo(() => {
    if (!awardSearchOpen) return [];
    const q = awardSearchQuery.trim().toLowerCase();
    const list = q
      ? roster.filter((r) => r.character_name.toLowerCase().includes(q))
      : roster;
    return list.slice(0, 8);
  }, [awardSearchOpen, awardSearchQuery, roster]);

  const submitManualAward = async () => {
    if (!awardCharId || !selectedItem) return;
    setAwardSubmitting(true);
    try {
      const note = awardNote.trim();
      const res = await fetch(`${API_URL}/guilds/${encodeURIComponent(guildId)}/loot-awards`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          character_id: awardCharId,
          external_item_id: String(selectedItem.external_item_id),
          loot_item_id: selectedItem.id,
          awarded_at: new Date().toISOString(),
          guild_raid_id: raidId,
          officer_note: note || undefined,
          heat_tag: awardHeatTag,
        }),
      });
      const b = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(b.error || `HTTP ${res.status}`);
      showToast?.("Award recorded.", "success");
      cancelAwardDraft();
      load();
    } catch (e: any) {
      showToast?.(e?.message || "Award failed", "error");
    } finally {
      setAwardSubmitting(false);
    }
  };

  const awardBar =
    awardCharId && selectedItem ? (
      <div className="fixed inset-x-0 bottom-0 z-[90] border-t border-emerald-500/35 bg-[var(--c-surface)] backdrop-blur-md shadow-[0_-12px_40px_rgba(0,0,0,0.55)] pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700/90 dark:text-emerald-300/90">Confirm award</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[var(--c-ink)]">
              <span className="font-semibold text-[var(--c-ink)] truncate max-w-[min(100%,14rem)]">{selectedItem.name}</span>
              <span className="text-[var(--c-ink-3)] hidden sm:inline">→</span>
              <span className="flex items-center gap-2 min-w-0">
                {pickedRosterRow ? (
                  <CouncilSeatIcon
                    className={pickedRosterRow.class_name}
                    roleLabel={pickedRosterRow.role_label}
                    charData={candidateById.get(pickedRosterRow.character_id)}
                    fields={gameFields}
                  />
                ) : null}
                <span className="font-bold text-[var(--c-ink)] truncate">
                  {pickedRosterRow?.character_name || "Unknown"}
                </span>
                {pickedListTag ? (
                  <span
                    className="rounded border border-amber-500/35 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800 dark:text-amber-100 shrink-0"
                    title={pickedViaItemName ? `BIS via ${pickedViaItemName}` : undefined}
                  >
                    List: {pickedListTag}
                    {pickedViaItemName ? (
                      <span className="ml-1 font-normal normal-case text-amber-800/70 dark:text-amber-100/70">
                        · via {pickedViaItemName}
                      </span>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-[10px] text-[var(--c-ink-3)] shrink-0">No list tag for this item</span>
                )}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)]">
                Heat impact
                <InfoTip>
                  <p className="text-[var(--c-ink-2)]">
                    Heat applied ={" "}
                    <span className="font-semibold text-[var(--c-ink)]">tier × slot weight × time decay</span>.
                    Defaults to the player&apos;s own list tag; off-list awards default to{" "}
                    <span className="font-semibold text-[var(--c-ink)]">none</span> (zero heat).
                  </p>
                </InfoTip>
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {([
                  { tag: "none", label: "None", hint: "0" },
                  { tag: "sidegrade", label: "Sidegrade", hint: "≈−3" },
                  { tag: "upgrade", label: "Upgrade", hint: "≈−12" },
                  { tag: "bis", label: "BIS", hint: "≈−30" },
                ] as const).map((opt) => {
                  const active = awardHeatTag === opt.tag;
                  return (
                    <button
                      key={opt.tag}
                      type="button"
                      onClick={() => setAwardHeatTag(opt.tag)}
                      disabled={awardSubmitting}
                      className={
                        "rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition disabled:opacity-40 " +
                        (active
                          ? "border-emerald-400/60 bg-emerald-500/20 text-emerald-800 dark:text-emerald-100"
                          : "border-[var(--c-line)] bg-[var(--c-bg-veil)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)]")
                      }
                    >
                      {opt.label}
                      <span className="ml-1 font-normal text-[var(--c-ink-3)]">{opt.hint}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)]">
              Officer note <span className="font-normal text-[var(--c-ink-4)]">(optional)</span>
              <input
                value={awardNote}
                onChange={(e) => setAwardNote(e.target.value)}
                placeholder="e.g. LC vote, prio reason…"
                className="mt-1 w-full max-w-md rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] px-3 py-2 text-xs text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)]"
              />
            </label>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={cancelAwardDraft}
              disabled={awardSubmitting}
              className="rounded-lg border border-[var(--c-line-strong)] bg-[var(--c-sunken)] px-4 py-2.5 text-xs font-bold text-[var(--c-ink-2)] hover:bg-[var(--c-sunken)] disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submitManualAward()}
              disabled={awardSubmitting}
              className="rounded-lg bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white shadow-lg shadow-emerald-900/40 hover:bg-emerald-500 disabled:opacity-40"
            >
              {awardSubmitting ? "Saving…" : "Confirm award"}
            </button>
          </div>
        </div>
      </div>
    ) : null;

  const compareDockOpen = pinnedForCompare.length > 0;
  const awardBarOpen = awardCharId != null && selectedItem != null;
  const bottomPadClass =
    awardBarOpen && compareDockOpen ? "pb-56" : awardBarOpen ? "pb-32" : compareDockOpen ? "pb-44" : "pb-16";

  const compareDock =
    compareDockOpen ? (
      <div
        className={`fixed inset-x-0 z-[86] border-t border-violet-400/25 bg-[var(--c-surface)] backdrop-blur-xl shadow-[0_-12px_48px_rgba(0,0,0,0.55)] ${
          awardBarOpen ? "bottom-[7.25rem]" : "bottom-0"
        } pb-[max(0.5rem,env(safe-area-inset-bottom))]`}
      >
        <div className="max-w-[100vw] mx-auto px-3 sm:px-4 pt-2.5 pb-2">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700/90 dark:text-violet-300/90">Compare players</div>
              <p className="text-[10px] text-[var(--c-ink-3)] mt-0.5 hidden sm:block">
                Pinned loot history stays open — scroll horizontally if needed.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPinnedForCompare([])}
              className="shrink-0 rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] px-3 py-1.5 text-[10px] font-bold uppercase text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)]"
            >
              Clear pins
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory">
            {pinnedForCompare.map((cid) => {
              const row = roster.find((r) => r.character_id === cid);
              if (!row) return null;
              const listTag =
                selectedLootId != null
                  ? bisForSelected.get(normCharacterIdForBisMap(cid))?.tag ?? null
                  : null;
              const heatHex = heatColorByCharacterId.get(cid) ?? "#9d9d9d";
              return (
                <div
                  key={cid}
                  className="snap-start shrink-0 w-[min(18rem,calc(100vw-2.5rem))] max-h-[min(42vh,22rem)] flex flex-col rounded-2xl border border-violet-500/20 bg-gradient-to-b from-violet-950/30 to-[var(--c-surface)] p-3 shadow-lg"
                >
                  <div className="flex items-start justify-between gap-2 mb-2 shrink-0">
                    <div className="min-w-0">
                      <div className="font-bold text-[var(--c-ink)] truncate text-sm">{row.character_name}</div>
                      <div className="text-[10px] text-[var(--c-ink-3)] truncate">
                        {row.class_name || "—"}
                        {row.role_label ? ` · ${row.role_label}` : ""}
                      </div>
                      <div
                        className="mt-1.5 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-mono font-semibold tabular-nums"
                        style={{
                          color: tc(heatHex),
                          borderColor: `${heatHex}40`,
                          backgroundColor: `${heatHex}15`,
                        }}
                      >
                        {row.heat}
                        <span className="text-[8px] font-bold uppercase ml-1 tracking-wide opacity-55">heat</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove ${row.character_name} from compare`}
                      onClick={() => setPinnedForCompare((p) => p.filter((x) => x !== cid))}
                      className="shrink-0 rounded-lg border border-[var(--c-line)] px-2 py-1 text-xs text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)]"
                    >
                      ×
                    </button>
                  </div>
                  <div className="overflow-y-auto min-h-0 flex-1 pr-0.5 -mr-0.5">
                    <LootHistoryPanelContent
                      row={row}
                      raidId={raidId}
                      selectedItem={selectedItem}
                      listTag={listTag}
                      showTitle={false}
                      showFootnote={false}
                      heatColorHex={heatHex}
                      canAssign={canAssign}
                      onRevoke={handleRevokeAward}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div className={`max-w-6xl mx-auto space-y-6 animate-in fade-in duration-200 ${bottomPadClass}`}>
      {awardBar ? createPortal(awardBar, document.body) : null}
      {compareDock ? createPortal(compareDock, document.body) : null}
      {rosterTip ? (
        <RosterLootHistoryPopover
          row={rosterTip.row}
          raidId={raidId}
          selectedItem={selectedItem}
          listTag={rosterTip.listTag}
          heatColorHex={heatColorByCharacterId.get(rosterTip.row.character_id) ?? "#9d9d9d"}
          anchor={{ left: rosterTip.left, top: rosterTip.top }}
          onPointerEnter={clearRosterTipTimer}
          onPointerLeave={scheduleHideRosterTip}
          canAssign={canAssign}
          onRevoke={handleRevokeAward}
        />
      ) : null}

<div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={onClose}
            className="mb-3 text-[11px] font-bold uppercase tracking-wider text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"
          >
            ← Back
          </button>
          <div className="flex items-center">
            <h2 className="text-3xl text-[var(--c-ink)]" style={DISPLAY}>Loot council</h2>
            <HeatHelpInfo />
          </div>
          <p className="text-sm text-[var(--c-ink-3)] mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{raidName}</span>
            {!canAssign && (
              <span
                className="inline-flex items-center rounded-md border border-[var(--c-line)] bg-[var(--c-sunken)] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)]"
                title="Only officers, raid leaders, and loot masters can assign gear."
              >
                View only
              </span>
            )}
          </p>
        </div>
        {canAssign && (
          <button
            type="button"
            onClick={() => void openSyncModal()}
            className="shrink-0 rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-100 hover:bg-amber-500/20"
            title="Encode this raid's recent awards as a Rallify addon paste code"
          >
            Sync to addon
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-20 text-center text-sm text-[var(--c-ink-3)]">Loading roster & heat…</div>
      ) : err ? (
        <div className="rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-800 dark:text-red-200">{err}</div>
      ) : (
        <>
          {/* Session focus — current decision context */}
          <div className="rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.08] via-[var(--c-surface)] to-violet-950/20 px-4 py-4 shadow-[inset_0_1px_0_rgba(251,191,36,0.12)]">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800/80 dark:text-amber-200/80 mb-2">Current council focus</div>
            {!selectedItem ? (
              <p className="text-sm text-[var(--c-ink-2)] leading-relaxed">
                <span className="text-[var(--c-ink)] font-semibold">Step 1:</span> Choose a piece from the loot cards. The roster stays
                sorted by <span className="text-amber-800/90 dark:text-amber-200/90 font-medium">heat</span> until you pick an item; then it narrows to
                players who tagged that item on their BIS list.
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap items-start gap-3 justify-between">
                  <div className="min-w-0 flex-1 max-w-full">
                    <div className="text-xs text-[var(--c-ink-3)] uppercase tracking-wider font-bold">Deciding</div>
                    <a
                      key={`council-focus-${selectedItem.id}-${selectedItem.external_item_id}`}
                      href={wowheadItemUrl(selectedItem.external_item_id, gameCode)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-base sm:text-lg font-bold text-sky-700 dark:text-sky-300 hover:underline block truncate max-w-full"
                      data-wh-rename-link="false"
                    >
                      {selectedItem.name}
                    </a>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] text-[var(--c-ink-3)]">
                      <span className="font-mono">#{selectedItem.external_item_id}</span>
                      {selectedItem.boss_name ? (
                        <span>
                          <span className="text-[var(--c-ink-4)]">Boss</span> {selectedItem.boss_name}
                        </span>
                      ) : null}
                      {selectedItem.slot ? (
                        <span>
                          <span className="text-[var(--c-ink-4)]">Slot</span> {selectedItem.slot}
                        </span>
                      ) : null}
                      {tokenChildren.length > 0 ? (
                        <span
                          className="rounded-md border border-amber-400/35 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-100"
                          title={tokenChildren.map((c) => c.name).join(", ")}
                        >
                          Token · redeems {tokenChildren.length} piece{tokenChildren.length === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                  <LootCouncilAiPanel
                      raidId={raidId}
                      canAssign={canAssign}
                      candidateNames={aiCandidateNames}
                      selectedItem={selectedItem ? {
                        id: selectedItem.id,
                        name: selectedItem.name,
                        external_item_id: selectedItem.external_item_id,
                        slot: selectedItem.slot,
                      } : null}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedLootId(null);
                        setAwardCharId(null);
                        setAwardNote("");
                      }}
                      className="text-[10px] font-bold uppercase text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)] border border-[var(--c-line)] rounded-lg px-3 py-1.5 hover:bg-[var(--c-sunken)]"
                    >
                      Clear item
                    </button>
                  </div>
                </div>
                {interestBreakdown && interestBreakdown.total > 0 ? (
                  <div className="flex flex-wrap gap-2 text-[11px]">
                    <span className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-2.5 py-1 text-[var(--c-ink-2)]">
                      <span className="text-[var(--c-ink-3)]">Interested</span>{" "}
                      <span className="font-bold text-[var(--c-ink)] tabular-nums">{interestBreakdown.total}</span>
                    </span>
                    {interestBreakdown.bis > 0 ? (
                      <span className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-amber-800 dark:text-amber-100 font-bold">
                        BIS {interestBreakdown.bis}
                      </span>
                    ) : null}
                    {interestBreakdown.upgrade > 0 ? (
                      <span className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-emerald-800 dark:text-emerald-100 font-bold">
                        Upgrade {interestBreakdown.upgrade}
                      </span>
                    ) : null}
                    {interestBreakdown.sidegrade > 0 ? (
                      <span className="rounded-lg border border-violet-500/30 bg-violet-500/10 px-2.5 py-1 text-violet-800 dark:text-violet-100 font-bold">
                        Sidegrade {interestBreakdown.sidegrade}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-[11px] text-[var(--c-ink-3)]">No roster members have this item on their BIS list — use full roster or award manually after discussion.</p>
                )}
                <div className="text-[11px] text-[var(--c-ink-3)] border-t border-[var(--c-line)] pt-2">
                  <span className="text-[var(--c-ink-3)]">Winner draft:</span>{" "}
                  {pickedRosterRow ? (
                    <span className="text-[var(--c-ink)] font-semibold">{pickedRosterRow.character_name}</span>
                  ) : canAssign ? (
                    <span className="italic">Pick a player from the roster → then confirm in the bar below.</span>
                  ) : (
                    <span className="italic">Only officers, raid leaders, and loot masters can assign gear.</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="grid gap-6 lg:gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
            <div className="rounded-3xl border border-[var(--c-line-soft)] bg-gradient-to-b from-[var(--c-surface)] to-[var(--c-surface)] shadow-[0_24px_60px_rgba(0,0,0,0.4)] overflow-hidden flex flex-col min-h-[320px]">
              <div className="px-4 py-4 border-b border-[var(--c-line-soft)] bg-[var(--c-bg-veil)]">
                <div className="text-xs font-semibold text-[var(--c-ink)] tracking-tight">Loot for this raid</div>
                <p className="text-[10px] text-[var(--c-ink-3)] mt-1 leading-relaxed">
                  Full-width cards — filter by boss or search, then pick an item to narrow the roster.
                </p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-stretch">
                  <input
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    placeholder="Search name, boss, or item ID…"
                    className="flex-1 min-w-0 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-3 py-2.5 text-xs text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] shadow-inner"
                  />
                  <select
                    value={bossFilterKey ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBossFilterKey(v === "" ? null : v);
                    }}
                    aria-label="Filter by boss"
                    className="w-full sm:w-[13.5rem] shrink-0 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-3 py-2.5 text-xs text-[var(--c-ink)]"
                  >
                    <option value="">All bosses</option>
                    {bossFilterOptions.map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex-1 min-h-[22vh] overflow-y-auto max-h-[min(55vh,40rem)] p-2.5 flex flex-col gap-2">
                {filteredItems.map((it) => {
                  const active = selectedLootId != null && sameLootRowId(selectedLootId, it.id);
                  const bar = lootQualityBarClass(it.quality);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => {
                        setSelectedLootId(active ? null : it.id);
                        if (active) {
                          setAwardCharId(null);
                          setAwardNote("");
                        }
                      }}
                      className={`group relative flex flex-col items-stretch h-auto w-full min-w-0 text-left rounded-xl border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/45 isolate overflow-visible ${
                        active
                          ? "border-amber-400/45 bg-gradient-to-br from-amber-500/14 to-violet-950/25 shadow-[inset_0_1px_0_rgba(251,191,36,0.12)] ring-1 ring-amber-400/15"
                          : "border-[var(--c-line-soft)] bg-[var(--c-bg-veil)] hover:bg-[var(--c-sunken)] hover:border-[var(--c-line)]"
                      }`}
                    >
                      <div className={`h-1 w-full shrink-0 rounded-t-xl bg-gradient-to-r ${bar}`} aria-hidden />
                      <div className="flex flex-col gap-1.5 p-2.5 pt-2 min-w-0">
                        {/* Anchor is rendered `inline` so its click hitbox hugs
                            the text glyphs only — empty space to the right of
                            short names falls through to the parent "select item"
                            button instead of opening Wowhead. */}
                        <div className="min-w-0 text-xs leading-snug line-clamp-2 break-words [overflow-wrap:anywhere]">
                          <a
                            key={`loot-card-wh-${it.id}-${it.external_item_id}`}
                            href={wowheadItemUrl(it.external_item_id, gameCode)}
                            target="_blank"
                            rel="noreferrer"
                            className="font-semibold text-sky-700 dark:text-sky-300 group-hover:text-sky-800 dark:group-hover:text-sky-200 hover:underline"
                            data-wh-rename-link="false"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {it.name}
                          </a>
                        </div>
                        <div className="flex flex-wrap gap-1.5 items-center">
                          {it.boss_name ? (
                            <span className="text-[9px] font-medium text-[var(--c-ink-3)] bg-[var(--c-surface)] rounded-md px-1.5 py-0.5 truncate max-w-full border border-[var(--c-line-soft)]">
                              {it.boss_name}
                            </span>
                          ) : null}
                          {it.slot ? (
                            <span className="text-[9px] text-violet-800/85 dark:text-violet-200/85 bg-violet-500/14 rounded-md px-1.5 py-0.5 border border-violet-400/15">
                              {it.slot}
                            </span>
                          ) : null}
                          <span className="text-[9px] text-[var(--c-ink-4)] font-mono">#{it.external_item_id}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
                {filteredItems.length === 0 && (
                  <div className="text-center text-xs text-[var(--c-ink-4)] py-10">No items match.</div>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-[var(--c-line-soft)] bg-gradient-to-b from-[var(--c-surface)] to-[var(--c-surface)] shadow-[0_24px_60px_rgba(0,0,0,0.4)] overflow-hidden flex flex-col min-h-[320px]">
              <div className="px-4 py-4 border-b border-[var(--c-line-soft)] bg-[var(--c-bg-veil)] flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-[var(--c-ink)] tracking-tight">
                    {selectedLootId != null ? "Who wants this?" : "Roster"}
                  </div>
                  <p className="text-[10px] text-[var(--c-ink-3)] mt-1 leading-relaxed">
                    Sorted by heat · hover a player for loot history ·{" "}
                    <span className="text-violet-800/90 dark:text-violet-200/90">Pin</span> up to {COMPARE_PIN_MAX} for a live compare tray
                  </p>
                  {selectedLootId != null && selectedItem && (
                    <div className="text-[11px] text-amber-800/70 dark:text-amber-200/70 mt-1.5 font-medium truncate">{selectedItem.name}</div>
                  )}
                </div>
                {selectedLootId != null && (
                  <div className="flex shrink-0 items-center gap-2 self-start">
                    {canAssign && (
                      <span className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setAwardSearchOpen((v) => !v);
                            setAwardSearchQuery("");
                          }}
                          className={
                            "rounded-xl border px-3 py-1.5 text-[10px] font-bold uppercase transition " +
                            (awardSearchOpen
                              ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-800 dark:text-emerald-200"
                              : "border-emerald-500/30 text-emerald-700/90 dark:text-emerald-300/90 hover:bg-emerald-500/10")
                          }
                        >
                          Award someone else
                        </button>
                        <InfoTip align="right">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700/85 dark:text-emerald-300/85">
                            Award other
                          </div>
                          <p className="mt-1 text-[var(--c-ink-2)]">
                            Search the full roster to give this item to a player who
                            didn&apos;t put it on their BIS list. Their heat defaults to{" "}
                            <span className="font-semibold text-[var(--c-ink)]">none</span> — set a
                            tier in the confirm bar if it should cost heat.
                          </p>
                        </InfoTip>
                      </span>
                    )}
                    <button
                      type="button"
                      className="rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)] px-3 py-1.5 text-[10px] font-bold uppercase text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken)]"
                      onClick={() => {
                        setSelectedLootId(null);
                        cancelAwardDraft();
                      }}
                    >
                      Clear item
                    </button>
                  </div>
                )}
              </div>
              {canAssign && selectedLootId != null && awardSearchOpen ? (
                <div className="px-4 py-3 border-b border-[var(--c-line-soft)] bg-emerald-500/[0.04]">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-emerald-800/80 dark:text-emerald-200/80 mb-1.5">
                    Award to someone not on the list
                  </label>
                  <input
                    autoFocus
                    value={awardSearchQuery}
                    onChange={(e) => setAwardSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setAwardSearchOpen(false);
                        setAwardSearchQuery("");
                      } else if (e.key === "Enter" && awardSearchResults.length > 0) {
                        pickPlayerForAward(awardSearchResults[0].character_id);
                      }
                    }}
                    placeholder="Type a character name…"
                    className="w-full rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] px-3 py-2 text-sm text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] focus:outline-none focus:border-emerald-400/45"
                  />
                  <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-[var(--c-line-soft)] divide-y divide-[var(--c-line-soft)]">
                    {awardSearchResults.map((r) => {
                      const tag = defaultHeatTagFor(r.character_id);
                      return (
                        <button
                          key={r.character_id}
                          type="button"
                          onClick={() => pickPlayerForAward(r.character_id)}
                          className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-[var(--c-sunken)]"
                        >
                          <span className="min-w-0">
                            <span
                              className="block truncate text-sm font-semibold"
                              style={{ color: tc(getWowClassColor(r.class_name)) }}
                            >
                              {r.character_name}
                            </span>
                            <span className="block truncate text-[10px] text-[var(--c-ink-3)]">
                              {r.class_name || "—"}
                              {r.role_label ? ` · ${r.role_label}` : ""}
                            </span>
                          </span>
                          <span className="shrink-0 text-[10px] font-bold uppercase text-[var(--c-ink-3)]">
                            {tag === "none" ? "no list tag" : `list: ${tag}`}
                          </span>
                        </button>
                      );
                    })}
                    {awardSearchResults.length === 0 && (
                      <div className="px-3 py-3 text-center text-[11px] text-[var(--c-ink-4)]">
                        No roster members match.
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
              <div
                className="flex-1 overflow-y-auto max-h-[55vh] p-3 space-y-2.5"
                onScroll={() => {
                  clearRosterTipTimer();
                  setRosterTip(null);
                }}
              >
                {displayedRoster.map((r) => {
                  const hit = bisForSelected.get(normCharacterIdForBisMap(r.character_id));
                  const tag = hit?.tag;
                  const viaItemName = hit?.viaItem?.name ?? null;
                  const charData = candidateById.get(r.character_id);
                  const classColor = getWowClassColor(r.class_name);
                  const heatHex = heatColorByCharacterId.get(r.character_id) ?? "#9d9d9d";
                  const isPinned = pinnedForCompare.includes(r.character_id);
                  const isPicked = awardCharId === r.character_id;

                  // Equipped-item lookup for the focused slot. When the council
                  // is deciding a tier token, `hit.viaItem.slot` carries the
                  // child piece's slot (e.g. HEAD for a token that redeems
                  // into a class-specific helm). Falls back to the focused
                  // item's own slot for non-tier rows.
                  const matchSlot =
                    selectedLootId != null
                      ? normEquipSlot(hit?.viaItem?.slot || selectedItem?.slot || "")
                      : "";
                  const equippedItem: EquippedItem | null =
                    matchSlot && r.equipment && Array.isArray(r.equipment.items)
                      ? r.equipment.items.find(
                          (p) => normEquipSlot(p.slot_type) === matchSlot
                        ) ?? null
                      : null;
                  const lastEquipMs = r.equipment_last_updated
                    ? new Date(r.equipment_last_updated).getTime()
                    : 0;
                  const equipmentIsStale =
                    !lastEquipMs ||
                    !Number.isFinite(lastEquipMs) ||
                    Date.now() - lastEquipMs > EQUIPMENT_STALE_MS;
                  const equipmentNeedsSync = !equippedItem || equipmentIsStale;
                  const isSyncingEquipment = syncingEquipmentByChar.has(r.character_id);

                  return (
                    <article
                      key={r.character_id}
                      className={`relative rounded-2xl border transition-colors ${
                        isPicked
                          ? "border-emerald-400/35 bg-emerald-500/[0.08] shadow-[inset_0_0_0_1px_rgba(52,211,153,0.12)]"
                          : isPinned
                            ? "border-violet-400/35 bg-violet-500/10 dark:bg-violet-950/25"
                            : "border-[var(--c-line-soft)] bg-[var(--c-bg-veil)] hover:bg-[var(--c-sunken)]"
                      }`}
                      style={{ borderLeft: `3px solid ${classColor}` }}
                      onMouseEnter={(e) => {
                        clearRosterTipTimer();
                        const rect = e.currentTarget.getBoundingClientRect();
                        const panelW = 304;
                        let left = rect.right + 8;
                        if (left + panelW > window.innerWidth - 8) {
                          left = Math.max(8, rect.left - panelW - 8);
                        }
                        const top = rect.top + rect.height / 2;
                        showRosterTip({
                          row: r,
                          listTag:
                            selectedLootId != null
                              ? bisForSelected.get(normCharacterIdForBisMap(r.character_id))?.tag ?? null
                              : null,
                          left,
                          top,
                        });
                      }}
                      onMouseLeave={scheduleHideRosterTip}
                    >
                      <div className="p-3 flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <CouncilSeatIcon
                            className={r.class_name}
                            roleLabel={r.role_label}
                            charData={charData}
                            fields={gameFields}
                          />
                          <div className="min-w-0">
                            <div className="font-semibold text-[var(--c-ink)] truncate text-sm">{r.character_name}</div>
                            <div className="text-[10px] text-[var(--c-ink-3)] truncate">
                              {r.class_name || "—"}
                              {r.role_label ? ` · ${r.role_label}` : ""}
                              {r.user_display_name ? ` · ${r.user_display_name}` : ""}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 shrink-0">
                          <span
                            className="inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-mono font-semibold tabular-nums"
                            style={{
                              color: tc(heatHex),
                              borderColor: `${heatHex}40`,
                              backgroundColor: `${heatHex}15`,
                            }}
                          >
                            {r.heat}
                            <span className="text-[8px] font-bold uppercase ml-1.5 tracking-wide opacity-55">heat</span>
                          </span>
                          {selectedLootId != null && tag ? (
                            <span
                              className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800 dark:text-amber-100"
                              title={viaItemName ? `BIS via ${viaItemName}` : undefined}
                            >
                              {tag}
                              {viaItemName ? (
                                <span className="ml-1 font-normal normal-case text-amber-800/70 dark:text-amber-100/70">
                                  · via {viaItemName}
                                </span>
                              ) : null}
                            </span>
                          ) : null}
                          {selectedLootId != null && selectedItem ? (
                            equippedItem && !equipmentIsStale ? (
                              <EquippedSlotIcon
                                item={equippedItem}
                                gameCode={gameCode}
                                equipmentItems={r.equipment?.items ?? []}
                              />
                            ) : canAssign ? (
                              <EquippedSlotSyncButton
                                isLoading={isSyncingEquipment}
                                isStale={equipmentIsStale && !!equippedItem}
                                onSync={() => handleSyncEquipment(r.character_id)}
                              />
                            ) : null
                          ) : null}
                          <button
                            type="button"
                            aria-pressed={isPinned}
                            aria-label={isPinned ? "Remove from compare" : "Pin for compare"}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              toggleComparePin(r.character_id);
                            }}
                            className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${
                              isPinned
                                ? "border-violet-400/50 bg-violet-500/20 text-violet-800 dark:text-violet-100"
                                : "border-[var(--c-line)] text-[var(--c-ink-3)] hover:border-violet-400/35 hover:text-violet-800/90 dark:hover:text-violet-200/90"
                            }`}
                          >
                            {isPinned ? "Pinned" : "Pin"}
                          </button>
                          {canAssign && selectedLootId != null && selectedItem ? (
                            <button
                              type="button"
                              className={`rounded-full text-[10px] font-bold uppercase px-3 py-1 border transition-colors ${
                                isPicked
                                  ? "border-emerald-400/50 bg-emerald-500/25 text-emerald-800 dark:text-emerald-100"
                                  : "border-emerald-500/30 text-emerald-700/90 dark:text-emerald-300/90 hover:bg-emerald-500/10"
                              }`}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                if (awardCharId === r.character_id) cancelAwardDraft();
                                else pickPlayerForAward(r.character_id);
                              }}
                            >
                              {isPicked ? "Picked" : "Pick"}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </article>
                  );
                })}
                {displayedRoster.length === 0 && (
                  <div className="text-center text-xs text-[var(--c-ink-4)] py-10 px-4 rounded-2xl border border-dashed border-[var(--c-line)] bg-[var(--c-bg-veil)]">
                    {selectedLootId != null
                      ? "No one has this item on their BIS list."
                      : "No roster assignments."}
                  </div>
                )}
              </div>
            </div>
          </div>

          {canAssign && (
            <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4 ring-1 ring-[var(--c-line-soft)]">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)] mb-3">RCLC import</div>
              <GuildLootImportsTab guildId={guildId} gameCode={gameCode} members={importMembers} />
            </div>
          )}
        </>
      )}

      {/* ── Sync-to-addon modal ── */}
      {syncOpen && (() => {
        const awards = syncAwards ?? [];
        const count = awards.length;
        const code = toRallifyLootCode({ raidId, raidName, awards });
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setSyncOpen(false)}
          >
            <div
              className="w-full max-w-2xl rounded-2xl border border-[var(--c-line)] bg-[var(--c-sunken)] p-5 space-y-4 max-h-[88vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div>
                <h2 className="text-sm font-bold text-[var(--c-ink)]">Sync awards to Rallify addon</h2>
                <p className="text-[11px] text-[var(--c-ink-3)] mt-2 leading-relaxed">
                  Install{" "}
                  
                    <a href="https://www.curseforge.com/wow/addons/rallify"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-700/90 dark:text-amber-300/90 underline hover:text-amber-800 dark:hover:text-amber-200"
                  >
                    Rallify
                  </a>
                  , type{" "}
                  <code className="px-1 py-0.5 rounded bg-[var(--c-sunken-2)] text-amber-800/90 dark:text-amber-200/90 font-mono">/rly</code>{" "}
                  in WoW, open the <em className="not-italic text-amber-800/90 dark:text-amber-200/90">Loot</em> tab and paste
                  the code below. Awards from the last 2 hours of this raid are included — older
                  ones are past the BoP trade window.
                </p>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto">
                {syncLoading ? (
                  <p className="text-[var(--c-ink-3)] text-sm py-8 text-center">Loading awards…</p>
                ) : syncError ? (
                  <p className="text-rose-800 dark:text-rose-200 text-sm py-8 text-center">{syncError}</p>
                ) : count === 0 ? (
                  <p className="text-[var(--c-ink-3)] text-sm py-8 text-center">
                    No awards in the last 2 hours for this raid — nothing to sync.
                  </p>
                ) : (
                  <ul className="space-y-1 text-[12px]">
                    {awards.map((a) => (
                      <li
                        key={a.id}
                        className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)]"
                      >
                        <span className="text-[var(--c-ink)] truncate flex-1">
                          {a.item_name || `#${a.external_item_id}`}
                        </span>
                        <span className="text-[var(--c-ink-3)] shrink-0">→</span>
                        <span className="text-[var(--c-ink)] font-semibold truncate flex-1 text-right">
                          {a.character_name}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] uppercase tracking-wider text-[var(--c-ink-3)]">
                    Preview · {count} award{count === 1 ? "" : "s"}
                  </span>
                </div>
                <textarea
                  readOnly
                  value={code}
                  rows={6}
                  className="w-full bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg px-3 py-2 text-[12px] text-[var(--c-ink)] font-mono outline-none resize-y break-all"
                  onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                />
                <p className="text-[10px] text-[var(--c-ink-3)] mt-1.5">
                  Paste the whole line — including the{" "}
                  <code className="px-1 py-0.5 rounded bg-[var(--c-sunken-2)] font-mono text-amber-800/90 dark:text-amber-200/90">RLY|LOOT|v1|</code>{" "}
                  prefix — into Rallify's <em className="not-italic text-amber-800/90 dark:text-amber-200/90">Paste code</em> dialog.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setSyncOpen(false)}
                  className="flex-1 h-9 rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-2)] text-xs font-bold"
                >
                  Close
                </button>
                <button
                  disabled={count === 0}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(code);
                      setSyncCopied(true);
                      setTimeout(() => setSyncCopied(false), 2000);
                    } catch {
                      showToast?.("Couldn't copy — select the text manually.", "error");
                    }
                  }}
                  className={`flex-1 h-9 rounded-lg text-xs font-bold transition-all disabled:opacity-40 ${
                    syncCopied
                      ? "bg-emerald-500/25 border border-emerald-500/40 text-emerald-800 dark:text-emerald-100"
                      : "bg-amber-500/20 border border-amber-500/40 text-amber-800 dark:text-amber-100 hover:bg-amber-500/30"
                  }`}
                >
                  {syncCopied ? "✓ Copied" : "Copy"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      </div>
    );
  }
